// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/lazyPage.ts
// 🔧 PURPOSE: Le pagine a pezzi. Prima l'app era un file solo (3,6 MB,
//             1 MB compresso) da scaricare e leggere tutto a ogni avvio,
//             anche per aprire solo "Il mio soggiorno" dal tag NFC. Ora ogni
//             pagina (tranne la Home) è un pezzo a sé, caricato quando serve.
//
//             `lazyPage` è React.lazy con `preload()`: il pezzo si può
//             chiedere PRIMA di mostrarlo (il percorso aperto, mentre gira
//             lo splash; le schede della barra, a riposo), così quando la
//             pagina arriva sullo schermo è già pronto. Un preload fallito
//             non lascia il segno: la pagina, quando serve, riprova; e
//             nemmeno un pezzo fallito mentre la pagina era aperta (offline):
//             rivedendola si riprova, non si ricarica tutta l'app.
//
//             Offline i pezzi li tiene il service worker (public/pwa-worker.js,
//             v18: tutti i file della build); se un pezzo non arriva lo dice
//             RouteBoundary.
// ============================================

import { createElement, lazy, type ComponentProps, type ComponentType, type LazyExoticComponent } from 'react';

export type LazyPage<T extends ComponentType<any>> = ComponentType<ComponentProps<T>> & {
  /** Chiede il pezzo adesso; non fallisce mai (al massimo riprova la pagina). */
  preload: () => Promise<void>;
};

/**
 * React.lazy si ricorda PER SEMPRE un pezzo fallito: rivedendo la pagina
 * rilancia lo stesso errore senza richiedere il file. Offline il pezzo non
 * arriva (giusto: "Questa pagina non si è caricata"), ma tornata la rete la
 * stessa pagina avrebbe ricaricato tutta l'app (o, dopo una ricarica
 * automatica nell'ultimo minuto, mostrato ancora il riquadro).
 *
 * Per questo la pagina è un componente che mostra il lazy "di turno", e un
 * pezzo fallito ne prepara uno nuovo che entra in gioco SOLO con
 * `retryFailedPages()` (RouteBoundary la chiama cambiando pagina, quando
 * toglie il riquadro): durante lo stesso giro React deve vedere sempre lo
 * stesso errore, se no riproverebbe all'infinito invece di mostrare il
 * riquadro. Webpack, a sua volta, dimentica il file fallito e lo richiede.
 */
const pendingRetries = new Set<() => void>();

/** I pezzi falliti si riprovano alla prossima apertura della loro pagina. */
export function retryFailedPages(): void {
  const renew = Array.from(pendingRetries);
  pendingRetries.clear();
  renew.forEach((fn) => fn());
}

export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): LazyPage<T> {
  let pending: Promise<{ default: T }> | null = null;
  let current: LazyExoticComponent<T>;
  const renew = () => { current = lazy(once); };
  const once = () => {
    if (!pending) {
      pending = load().catch((error) => {
        // Il prossimo tentativo (la pagina vera) riparte da capo.
        pending = null;
        pendingRetries.add(renew);
        throw error;
      });
    }
    return pending;
  };
  current = lazy(once);
  const Page = ((props: ComponentProps<T>) => createElement(current, props)) as LazyPage<T>;
  Page.preload = () => once().then(() => undefined, () => undefined);
  return Page;
}

/**
 * Il pezzo del percorso aperto, da chiedere subito all'avvio. `routes` va
 * dal più specifico al più generico: vale il primo prefisso che combacia
 * ("/extras/cart" prima di "/extras/").
 */
export function preloadForPath(
  pathname: string,
  routes: Array<[prefix: string, page: { preload: () => Promise<void> }]>,
): boolean {
  const path = pathname || '/';
  for (const [prefix, page] of routes) {
    const hit = prefix.endsWith('/') ? path.indexOf(prefix) === 0 : path === prefix;
    if (hit) {
      void page.preload();
      return true;
    }
  }
  return false;
}
